import React from 'react';
import * as TablerIcons from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useInfoCards } from '@/hooks/useInfoCards';
import { getMemberInfo } from '@/hooks/useMembers';
import MLoader from '@/components/ui/atoms/MLoader';
import IconDoctor from '@/components/icons/IconDoctor';
import IconVaccination from '@/components/icons/IconVaccination';
import IconPaw from '@/components/icons/IconPaw';
import IconTherapySession from '@/components/icons/IconTherapySession';
import IconLabTest from '@/components/icons/IconLabTest';
import { formatReminderDate } from '@/utils/date-utils';
import styles from '@/pages/Dashboard/components/Updates.module.css';

const Updates: React.FC = () => {
  const navigate = useNavigate();
  const loggedInMember = getMemberInfo(true);
  const memberId = loggedInMember.id;
  const { notifications, loading } = useInfoCards(memberId);

  // Use real backend data only
  const updates = notifications;

  if (loading) return <MLoader />;

  const getIconByType = (type: string): string => {
    switch (type) {
      case 'access':
        return 'user-check';
      case 'family':
        return 'users';
      case 'subscription':
        return 'credit-card';
      case 'reminder':
        return 'clipboard-check';
      case 'message':
        return 'message-circle';
      default:
        return 'bell';
    }
  };

  const getSubTypeIcon = (subType: string) => {
    switch (subType) {
      case 'doctor':
      case 'healthCheckup':
        return <IconDoctor size={20} />;
      case 'vaccination':
        return <IconVaccination size={20} />;
      case 'insurance':
        return <TablerIcons.IconShieldHeart size={20} />;
      case 'petCare':
        return <IconPaw size={20} />;
      case 'therapy':
        return <IconTherapySession size={20} />;
      case 'labTest':
        return <IconLabTest size={20} />;
      default:
        return null;
    }
  };

  const renderIcon = (size: number = 24, color?: string, type?: string) => {
    // Use type-based icon mapping
    const iconName = type ? getIconByType(type) : null;
    
    // Handle undefined or empty icon names
    if (!iconName) {
      return null;
    }
    
    // Convert kebab-case to PascalCase for Tabler Icons
    const pascalCaseName = iconName
      .split('-')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join('');
    const IconComponent = (TablerIcons as any)[`Icon${pascalCaseName}`];
    if (IconComponent) {
      return <IconComponent size={size} color={color} />;
    }
    return null;
  };

  return (
    <>
      {/* Custom Header */}
      <div className={styles.updatesHeader}>
        <button
          onClick={() => navigate('/dashboard')}
          className={styles.backButton}
        >
          <TablerIcons.IconChevronLeft size={24} color="#2A2A2A" />
          <span className={styles.backText}>
            Back
          </span>
        </button>
        
        <h2 className={styles.updatesTitle}>
          Your updates
        </h2>
      </div>
      
      <div className={styles.updates} style={{width: '100%', borderRadius: 0}}>
        {/* Update Cards - All updates without header */}
        {updates.length > 0 ? (
          updates.map((update, index) => (
            <div 
              key={update.id} 
              className={`${styles.updateCard} ${index === updates.length - 1 ? styles.lastUpdateCard : ''}`}
            >
              <div className={styles.updateContent}>
                {/* Icon on the left */}
                <div className={styles.iconContainer}>
                  <div className={styles.updateIcon}>
                    {renderIcon(24, undefined, update.notificationIndicator || update.notificationType || update.type)}
                  </div>
                  {update.unread && <div className={styles.unreadIndicator} />}
                </div>

                {/* Content section */}
                <div className={styles.textContent}>
                  <div className={styles.messageWithDate}>
                    <h3 
                      className={styles.updateTitle}
                      dangerouslySetInnerHTML={{ __html: update.message }}
                    />
                    {/* Show date horizontally only when no details have meta information */}
                    {update.createdAt && !update.details?.some(detail => detail.meta) && (
                      <span className={styles.horizontalDate}>{formatReminderDate(update.createdAt)}</span>
                    )}
                  </div>
                  
                  {/* Details section (conditional) */}
                  {update.details && update.details.length > 0 && (
                    <div className={styles.detailsSection}>
                      {update.details.map((detail, index) => {
                      const icon = getSubTypeIcon(detail.subType);
                      return (
                        <div key={`${update.id}-detail-${index}`} className={styles.detailItem}>
                          {/* Detail icon - only render if icon exists */}
                          {icon && (
                            <div className={styles.detailIcon}>
                              {icon}
                            </div>
                          )}
                          
                          {/* Detail content */}
                          <div>
                            <div className={styles.detailLabel}>{detail.message}</div>
                            {detail.meta && (
                              <div className={styles.metaInfo}>
                                {detail.meta}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className={styles.noUpdates}>
            <div className={styles.noUpdatesIcon}>
              {renderIcon(48, '#d1d5db', 'general')}
            </div>
            <p className={styles.noUpdatesText}>No Updates</p>
          </div>
        )}
      </div>
    </>
  );
};

export default Updates;
